/**
 * wim-lazyload.js
 * Reemplazo ligero de lazysizes basado en IntersectionObserver.
 *
 * Cubre el subconjunto de lazysizes que consume `theme.min.js` (no editable):
 *   - `window.lazySizes.loader.unveil(el)`      → carga inmediata forzada.
 *   - `window.lazySizes.autoSizer.checkElems()` → re-escaneo de elementos nuevos.
 *   - evento `lazyloaded` (bubbles) despachado sobre el elemento tras cargar.
 * Y las imágenes de fondo `data-bgset` del Liquid (width-set y media-syntax).
 *
 * NO gestiona <img> estáticas (esas usan loading="lazy" nativo).
 * Contrato completo documentado en el ticket MIL-8.
 */
(() => {
  'use strict';

  /** Clases por defecto si no existe window.lazySizesConfig. */
  const DEFAULTS = {
    lazyClass: 'Image--lazyLoad',
    loadingClass: 'Image--lazyLoading',
    loadedClass: 'Image--lazyLoaded'
  };
  const cfg = Object.assign({}, DEFAULTS, window.lazySizesConfig || {});

  const DPR_CAP = 2.5;
  const ROOT_MARGIN = '400px 0px';

  /** Ratio de píxel del dispositivo con tope defensivo. */
  const dpr = () => Math.min(window.devicePixelRatio || 1, DPR_CAP);

  /** Ancho renderizado del elemento en px CSS. */
  const renderedWidth = (el) => el.getBoundingClientRect().width || el.offsetWidth || 0;

  /**
   * ¿Es un elemento lazy pendiente? Cualquier elemento con lazyClass no procesado,
   * tenga fuente (data-src/data-bgset/data-bg) o sea reveal puro (sin fuente).
   */
  const isLazyPending = (el) => el.dataset.wimLazy !== 'done';

  /**
   * Parsea un set con width-descriptors ("url 750w, url 1000w") a [{url, w}].
   * Si un candidato no trae descriptor, w=0 (se tratará como fallback).
   */
  const parseWidthSet = (str) =>
    str.split(',').map((entry) => {
      const parts = entry.trim().split(/\s+/);
      const last = parts[parts.length - 1];
      const w = /^\d+w$/.test(last) ? parseInt(last, 10) : 0;
      const url = (w ? parts.slice(0, -1).join(' ') : parts.join(' ')).trim();
      return { url, w };
    });

  /**
   * Elige la URL cuyo width sea el menor >= necesario; si ninguno llega, el mayor.
   */
  const pickByWidth = (set, needed) => {
    const valid = set.filter((c) => c.url);
    if (!valid.length) return null;
    const sorted = valid.slice().sort((a, b) => a.w - b.w);
    const fit = sorted.find((c) => c.w >= needed);
    return (fit || sorted[sorted.length - 1]).url;
  };

  /**
   * Resuelve la URL de fondo desde data-bgset. Soporta:
   *  1) width-set:    "url 750w, url 1000w, url 1500w"
   *  2) media-syntax: "url_movil [(max-width: 640px)] | url_desktop"
   *     (cada tramo puede además contener width-descriptors).
   */
  const resolveBgset = (el) => {
    const raw = el.getAttribute('data-bgset');
    if (!raw) return null;
    const needed = renderedWidth(el) * dpr();

    if (raw.indexOf('|') === -1) {
      return pickByWidth(parseWidthSet(raw), needed);
    }

    const candidates = raw.split('|').map((c) => c.trim());
    const mediaRe = /\[([^\]]+)\]/;
    let segment;

    for (const c of candidates) {
      const m = c.match(mediaRe);
      if (m && window.matchMedia(m[1]).matches) {
        segment = c.replace(mediaRe, '').trim();
        break;
      }
    }
    if (segment === undefined) {
      const def = candidates.find((c) => !mediaRe.test(c));
      segment = (def || candidates[candidates.length - 1]).replace(mediaRe, '').trim();
    }
    return pickByWidth(parseWidthSet(segment), needed);
  };

  /**
   * Aplica la fuente a un IMG/IFRAME (u otro no-img) desde data-src.
   * Soporta plantillas RIAS con {width} + data-widths + data-sizes="auto".
   */
  const applySrc = (el) => {
    const dataSrc = el.getAttribute('data-src');
    const isImg = el.tagName === 'IMG';
    const dataSizes = el.getAttribute('data-sizes');

    if (isImg && dataSrc && dataSrc.indexOf('{width}') !== -1) {
      let widths = [];
      try {
        widths = JSON.parse(el.getAttribute('data-widths') || '[]');
      } catch (_) {
        widths = [];
      }
      const rendered = renderedWidth(el);
      const needed = rendered * dpr();

      if (widths.length) {
        el.setAttribute(
          'srcset',
          widths.map((w) => `${dataSrc.replace('{width}', w)} ${w}w`).join(', ')
        );
        const sorted = widths.slice().sort((a, b) => a - b);
        const chosen = sorted.find((w) => w >= needed) || sorted[sorted.length - 1];
        el.setAttribute('src', dataSrc.replace('{width}', chosen));
      } else {
        el.setAttribute('src', dataSrc.replace('{width}', Math.round(needed) || 1));
      }
      el.setAttribute(
        'sizes',
        dataSizes === 'auto' || !dataSizes ? `${Math.round(rendered)}px` : dataSizes
      );
      return;
    }

    // Sin {width}: srcset directo (solo img) + src plano; iframe/no-img solo src.
    if (isImg) {
      const dataSrcset = el.getAttribute('data-srcset');
      if (dataSrcset) el.setAttribute('srcset', dataSrcset);
      if (dataSizes && dataSizes !== 'auto') el.setAttribute('sizes', dataSizes);
    }
    if (dataSrc) el.setAttribute('src', dataSrc);
  };

  /**
   * Carga inmediata forzada de un elemento (API pública loader.unveil).
   * Gestiona el ciclo de clases y despacha `lazyloaded` al terminar.
   */
  const unveil = (el) => {
    if (!el || el.dataset.wimLazy === 'done') return;
    el.dataset.wimLazy = 'done';

    el.classList.remove(cfg.lazyClass);
    el.classList.add(cfg.loadingClass);

    const finish = () => {
      el.classList.remove(cfg.loadingClass);
      el.classList.add(cfg.loadedClass);
      el.dispatchEvent(new CustomEvent('lazyloaded', { bubbles: true }));
    };

    // Fondo múltiple (data-bgset): elige URL por ancho/media y pinta el fondo.
    if (el.hasAttribute('data-bgset')) {
      const url = resolveBgset(el);
      if (url) el.style.backgroundImage = `url("${url}")`;
      finish();
      return;
    }

    // Fondo único (data-bg, plugin unveilhooks): pinta directamente.
    if (el.hasAttribute('data-bg')) {
      const url = el.getAttribute('data-bg');
      if (url) el.style.backgroundImage = `url("${url}")`;
      finish();
      return;
    }

    // Fuente en atributo (IMG/IFRAME con data-src).
    if (el.hasAttribute('data-src')) {
      el.addEventListener('load', finish, { once: true });
      el.addEventListener('error', finish, { once: true });
      applySrc(el);
      return;
    }

    // Reveal puro: sin fuente, solo aplicar el flujo de clases para revelar.
    finish();
  };

  // ── Observadores ───────────────────────────────────────────────────────────
  const hasIO = 'IntersectionObserver' in window;

  const io = hasIO
    ? new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            obs.unobserve(entry.target);
            unveil(entry.target);
          });
        },
        { rootMargin: ROOT_MARGIN, threshold: 0 }
      )
    : null;

  /** Observa el elemento (o lo carga ya si no hay IntersectionObserver). */
  const observe = (el) => {
    if (!isLazyPending(el)) return;
    if (!io) {
      unveil(el);
      return;
    }
    if (el.dataset.wimObs === '1') return;
    el.dataset.wimObs = '1';
    io.observe(el);
  };

  /** Re-escanea el DOM en busca de elementos lazy (API autoSizer.checkElems). */
  const rescan = () => {
    document.querySelectorAll(`.${cfg.lazyClass}`).forEach(observe);
  };

  /**
   * Coalesce un único rescan por frame. En páginas con mucho DOM inyectado por
   * terceros esto evita recorrer subtrees en cada mutación (coste de main-thread);
   * un solo querySelectorAll por frame en lugar de N por mutación.
   */
  const schedule = window.requestAnimationFrame
    ? window.requestAnimationFrame.bind(window)
    : (fn) => setTimeout(fn, 16);
  let scanScheduled = false;
  const scheduleRescan = () => {
    if (scanScheduled) return;
    scanScheduled = true;
    schedule(() => {
      scanScheduled = false;
      rescan();
    });
  };

  // MutationObserver: detecta elementos lazy creados en runtime
  // (swatch de cards, galería PDP, iframe de vídeo). En cuanto ve un nodo
  // elemento añadido, programa un rescan diferido y deja de iterar.
  const mo = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) {
          scheduleRescan();
          return;
        }
      }
    }
  });

  // ── API pública (definir el global ANTES de cualquier trabajo async) ─────────
  window.lazySizes = {
    cfg,
    loader: { unveil },
    autoSizer: { checkElems: rescan }
  };

  // ── Init ─────────────────────────────────────────────────────────────────
  // Observar solo <body> (no <head>): evita disparos por scripts/links de
  // terceros inyectados en la cabecera, irrelevantes para imágenes lazy.
  // El script carga con `defer`, así que document.body ya existe.
  mo.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true
  });
  rescan();
})();
